import React from 'react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { DuotoneImage } from '@/components/site/DuotoneImage';
import { CityStrip } from '@/components/site/CityStrip';
import { PROCESS, STATS } from '@/data/site';
import { ABOUT_IMAGE } from '@/data/homeImages';

const PEOPLE = [
  { name: 'Aniket Bose', role: 'Founding partner · Strategy', location: 'Pune' },
  { name: 'Riya Shenoy', role: 'Design lead', location: 'Bengaluru' },
  { name: 'Devansh Khurana', role: 'Engineering lead', location: 'Pune' },
  { name: 'Kavya Iyer', role: 'Motion & interaction', location: 'Mumbai' },
  { name: 'Faizan Sheikh', role: 'AI / Platform', location: 'Hyderabad' },
  { name: 'Manjari Rao', role: 'Producer · Ops', location: 'Pune' },
];

const PRINCIPLES = [
  { k: 'Restraint', v: 'Most products fail by addition. We design by subtraction—if a screen survives the “does this earn its place?” review, it stays.' },
  { k: 'Numbered briefs', v: 'Every engagement starts with a one-page brief and three measurable outcomes. Vague success criteria are an unpaid invoice waiting to happen.' },
  { k: 'In the file', v: 'Designers and engineers work in the same file, in the same week. No throw-overs, no “handoff” meetings.' },
  { k: 'Quiet motion', v: 'Animation should make a product feel inevitable, not impressive. If a motion needs to be explained, it doesn’t belong.' },
  { k: 'Stay close', v: 'After launch we stay for a month. Bugs, polish, the second batch of analytics—we own them with you.' },
  { k: 'Say no', v: 'We turn down ~70% of inbound. Not arrogance—just a hard belief that taking on the wrong work makes the right work worse.' },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.3} />
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-40 pb-20 md:pb-28">
          <SectionLabel as="h1" num="01" data-testid="about-eyebrow">
            About NioDelta — Software Development Studio in Pune
          </SectionLabel>
          <WordReveal
            as="p"
            text="Twelve operators. One opinionated way of building."
            accent="opinionated"
            data-testid="about-headline"
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[80px] font-extrabold tracking-tightest leading-[1.02] text-balance max-w-5xl"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <Reveal>
                <p className="text-lg md:text-xl text-ink-muted leading-relaxed">
                  NioDelta started in 2017 as a two-person studio out of a flat in Kothrud, Pune. Eight years
                  later we’re twelve operators across three cities, building products for founders we like and
                  saying no to most of the rest.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal delay={150}>
                <p className="text-lg md:text-xl text-ink-text/90 leading-relaxed">
                  We are not an agency. We don’t have a sales team, account managers, or a deck library. We have
                  a partner who picks up the phone, a brief that gets shorter every quarter, and a builder’s
                  bias against PowerPoint.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO IMAGE BAND */}
      <section data-testid="about-image" className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <Reveal>
          <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-[20px] border border-white/[0.12]">
            <DuotoneImage src={ABOUT_IMAGE.src} alt={ABOUT_IMAGE.alt} duotone={0.4} />
            <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2.5 text-[10px] uppercase tracking-[0.28em] text-white/80 bg-black/30 backdrop-blur-sm border border-white/[0.12] rounded-full px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-violet" />
              Kothrud, Pune · since 2017
            </div>
          </div>
        </Reveal>
      </section>

      {/* STATS — asymmetric */}
      <section data-testid="about-stats" className="relative border-y border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="border-l border-white/[0.12] pl-5">
                <div className="text-4xl md:text-5xl font-bold tracking-tight tabular-nums">
                  {s.kpi}
                  <span className="text-violet">{s.suffix}</span>
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="02">Principles</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Six things we <span className="italic text-electric">do not</span> negotiate.
            </h2>
          </div>
          <div className="md:col-span-8">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {PRINCIPLES.map((p, i) => (
                <li key={p.k} className="py-7 grid grid-cols-1 md:grid-cols-12 gap-6">
                  <Reveal className="md:col-span-3" delay={i * 60}>
                    <div className="text-xs uppercase tracking-[0.24em] text-ink-muted tabular-nums">
                      0{i + 1}
                    </div>
                    <div className="mt-2 text-xl font-semibold tracking-tight">{p.k}</div>
                  </Reveal>
                  <Reveal className="md:col-span-9" delay={i * 60 + 70}>
                    <p className="text-ink-muted leading-relaxed">{p.v}</p>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="about-process" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <SectionLabel num="03">Process</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
                Five stages, <span className="italic text-violet">one file.</span>
              </h2>
              <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
                We use the same five-stage rhythm whether the engagement is six weeks or six months. The
                shape of the work is constant; only the depth changes.
              </p>
            </div>
            <div className="md:col-span-8 space-y-4">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 80}>
                  <div className="group rounded-[16px] border border-white/[0.12] bg-ink-bg p-6 md:p-7 transition-all duration-500 hover:border-white/25 hover:-translate-y-0.5">
                    <div className="flex items-baseline gap-6">
                      <div className="text-violet tabular-nums text-sm">{p.step}</div>
                      <div className="h-px w-12 bg-white/15" />
                      <div className="text-xl md:text-2xl font-semibold tracking-tight">{p.title}</div>
                    </div>
                    <p className="mt-4 ml-[68px] text-ink-muted leading-relaxed max-w-2xl">{p.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PEOPLE */}
      <section data-testid="about-people" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <SectionLabel num="04">People</SectionLabel>
              <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                A small bench. <span className="italic text-electric">No subcontractors.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {PEOPLE.map((p, i) => (
              <Reveal
                key={p.name}
                delay={i * 70}
                className={i % 4 === 0 ? 'md:col-span-5' : 'md:col-span-' + (i % 3 === 0 ? '4' : '4')}
              >
                <div
                  data-testid={`person-${p.name.split(' ')[0].toLowerCase()}`}
                  className="group h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 md:p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25"
                >
                  <div className="aspect-[5/3] rounded-[12px] mb-5 overflow-hidden border border-white/10"
                    style={{
                      background: `radial-gradient(circle at ${20 + i * 10}% ${30 + i * 8}%, rgba(108,92,231,0.45), transparent 55%), radial-gradient(circle at 80% 80%, rgba(75,159,255,0.35), transparent 50%), #0E0E15`,
                    }}
                  >
                    <div className="h-full w-full grid-bg opacity-30" />
                  </div>
                  <div className="text-xl font-semibold tracking-tight">{p.name}</div>
                  <div className="mt-1 text-sm text-ink-muted">{p.role}</div>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                    {p.location}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CityStrip num="05" className="bg-ink-surface/30" />

      <CTABand
        eyebrow="Working in public"
        headline="Want to know the people before the pitch?"
        sub="The discovery call is with a partner. No SDR. No account manager. The person on the call is the person on the project."
      />
    </div>
  );
}
